import { getInputHistoryPersistKey } from '@/helpers/persistScope';
import { useUserStore } from '@/stores/user';
import type { IInputHistoryOptions } from '@/types';

const HISTORY_LIMIT = 15;

type InputHistoryState = Record<string, unknown[]>;

let state: Ref<InputHistoryState> | null = null;

function readState(): InputHistoryState {
    const userStore = useUserStore();
    if (userStore.id == null) {
        return {};
    }

    try {
        const saved = localStorage.getItem(getInputHistoryPersistKey());
        if (!saved) return {};

        const parsed = JSON.parse(saved);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

        return Object.entries(parsed as Record<string, unknown>).reduce((acc, [key, value]) => {
            if (Array.isArray(value)) acc[key] = value;
            return acc;
        }, {} as InputHistoryState);
    } catch (e) {
        console.warn(`Failed to parse input history from "${getInputHistoryPersistKey()}"`, e);
        return {};
    }
}

function useState(): Ref<InputHistoryState> {
    if (!state) {
        state = ref(readState());
    }

    return state;
}

function persist(value: InputHistoryState) {
    const userStore = useUserStore();
    if (userStore.id == null) {
        return;
    }

    try {
        localStorage.setItem(getInputHistoryPersistKey(), JSON.stringify(value));
    } catch (e) {
        console.warn(`Failed to save input history to "${getInputHistoryPersistKey()}"`, e);
    }
}

/** Reload in-memory cache after login / user switch (SPA, no F5). */
export function resetInputHistoryState(): void {
    state = ref(readState());
}

/**
 * Keeps the last entered/selected values of a search input, shared by all inputs using the same key.
 * Values are stored per user in localStorage, newest first, limited to HISTORY_LIMIT per key.
 */
export function useInputHistory<T>(key: MaybeRefOrGetter<string | undefined>, options: IInputHistoryOptions<T> = {}) {
    const { getId, collapseTypedPrefix = false } = options;

    const sharedState = useState();
    const storageKey = computed(() => toValue(key) || '');
    const items = computed((): T[] => (storageKey.value ? ((sharedState.value[storageKey.value] ?? []) as T[]) : []));

    const identify = (item: T): string => (getId ? String(getId(item)) : String(item));

    function write(next: T[]) {
        if (!storageKey.value) return;

        const { [storageKey.value]: _dropped, ...rest } = sharedState.value;
        sharedState.value = next.length ? { ...rest, [storageKey.value]: next } : rest;
        persist(sharedState.value);
    }

    function remember(item: T) {
        if (item == null) return;
        if (typeof item === 'string' && !item.trim()) return;

        const id = identify(item);
        let kept = items.value.filter((existing) => identify(existing) !== id);

        // "abc" typed on the way to "abcdef" is noise, keep only the longest variant
        if (collapseTypedPrefix && typeof item === 'string') {
            const typed = item.toLowerCase();
            kept = kept.filter((existing) => typeof existing !== 'string' || !typed.startsWith(existing.toLowerCase()));
        }

        write([item, ...kept].slice(0, HISTORY_LIMIT));
    }

    function forget(item: T) {
        const id = identify(item);
        write(items.value.filter((existing) => identify(existing) !== id));
    }

    function clear() {
        write([]);
    }

    return {
        items,
        remember,
        forget,
        clear,
    };
}
